/**
 * Amtliche Luftbilder (DOP) der Bundesländer als hochauflösende Satelliten-/Luftbildebene (#19684).
 *
 * Die Dienste werden direkt im Browser per WMS 1.1.1 (EPSG:3857) abgefragt, ohne den REVA-GeoServer.
 * Die Bundesland-Zuordnung erfolgt über die ersten zwei Stellen des Gemeindeschlüssels (AGS).
 * Lizenz und Quellenvermerk je Land stammen aus den Capabilities der Dienste.
 */

export interface AerialImagerySource {
    /** WMS-Basis-URL (GetMap), inkl. Trennzeichen für weitere Parameter */
    url: string;
    /** WMS-Layername */
    layer: string;
    /** zusätzliche WMS-Parameter, z. B. ein fester Jahrgang per TIME */
    params?: Record<string, string>;
    /**
     * Fest eingestellter Jahrgang, falls URL, Layer oder Parameter einen bestimmten Jahrgang anfragen.
     * `aerialImageryJahrgang.test.ts` meldet, sobald der Dienst einen neueren anbietet.
     */
    year?: number;
    /** Quellenvermerk (HTML) gemäß Lizenz des Landes */
    attributions: string;
}

const CC_BY_4_0 = '<a target="_blank" href="https://creativecommons.org/licenses/by/4.0/">CC BY 4.0</a>';
const DL_DE_BY_2_0 = '<a target="_blank" href="https://www.govdata.de/dl-de/by-2-0">dl-de/by-2-0</a>';

/** Jahr des Datenbezugs für Quellenvermerke, die eines verlangen (die Dienste werden live abgefragt). */
const DATA_YEAR = new Date().getFullYear();

/**
 * Quellen nach den ersten zwei Stellen des Gemeindeschlüssels (AGS); ohne Eintrag gilt der ESA-Fallback.
 */
export const AERIAL_IMAGERY_BY_STATE_PREFIX: Readonly<Record<string, AerialImagerySource>> = {
    // Schleswig-Holstein: CC BY 4.0, DOP20, Vermerk laut Capabilities
    "01": {
        url: "https://dienste.gdi-sh.de/WMS_SH_DOP20col_OpenGBD?",
        layer: "sh_dop20_rgb",
        attributions: `© GeoBasis-DE/LVermGeo SH/${CC_BY_4_0}`,
    },
    // Hamburg: dl-de/by-2-0, True-DOP 20 cm. TIME fest auf 2024: die Zeitreihe enthält auch Satellitenjahrgänge
    // unter geschlossener Lizenz (2022 Pléiades), und der Default wandert sonst mit neuen Jahrgängen mit.
    "02": {
        url: "https://geodienste.hamburg.de/wms_dop_zeitreihe_belaubt?",
        layer: "dop_zeitreihe_belaubt",
        params: {TIME: "2024"},
        year: 2024,
        attributions: `Freie und Hansestadt Hamburg, Landesbetrieb Geoinformation und Vermessung (LGV), ${DL_DE_BY_2_0}`,
    },
    // Niedersachsen: CC BY 4.0, DOP20, Vermerk mit Rechteinhaber und Jahr
    "03": {
        url: "https://opendata.lgln.niedersachsen.de/doorman/noauth/dop_wms?",
        layer: "ni_dop20",
        attributions: `© LGLN (${DATA_YEAR}), ${CC_BY_4_0}`,
    },
    // Bremen: CC BY, DOP20 2023, Stadt Bremen und Bremerhaven
    "04": {
        url: "https://geodienste.bremen.de/wms_dop20_2023?",
        layer: "DOP20_2023_HB,DOP20_2023_BHV",
        year: 2023,
        attributions: "© Landesamt GeoInformation Bremen, CC BY",
    },
    // Nordrhein-Westfalen: dl-zero-de/2.0, keine Namensnennung nötig
    "05": {
        url: "https://www.wms.nrw.de/geobasis/wms_nw_dop?",
        layer: "nw_dop_rgb",
        attributions: "© Geobasis NRW",
    },
    // Hessen: dl-zero-de/2.0, DOP20
    "06": {
        url: "https://www.gds-srv.hessen.de/cgi-bin/lika-services/ogc-free-images.ows?language=ger&",
        layer: "he_dop20_rgb",
        attributions: "Luftbild: HVBG Hessen",
    },
    // Rheinland-Pfalz: dl-de/by-2-0, DOP20, Vermerk mit Jahr des Datenbezugs
    "07": {
        url: "https://geo4.service24.rlp.de/wms/rp_dop20.fcgi?",
        layer: "rp_dop20",
        attributions: `© GeoBasis-DE / <a target="_blank" href="http://www.lvermgeo.rlp.de">LVermGeoRP</a> (${DATA_YEAR}), ${DL_DE_BY_2_0}`,
    },
    // Baden-Württemberg bewusst nicht: der WMS liefert flackernd leere Bilder (Nagios-Check bleibt rot, #19684)
    // Bayern: CC BY 4.0, Open Data nur als DOP40 (40 cm)
    "09": {
        url: "https://geoservices.bayern.de/od/wms/dop/v1/dop40?language=ger&",
        layer: "by_dop40c",
        attributions: `© <a target="_blank" href="https://geodaten.bayern.de">Bayerische Vermessungsverwaltung</a>, ${CC_BY_4_0}`,
    },
    // Saarland: dl-de/by-2-0, DOP20 2025, Vermerk laut LVGL "© GeoBasis DE/LVGL-SL (Jahr)"
    "10": {
        url: "https://geoportal.saarland.de/freewms/dop2025?",
        layer: "sl_dop20_rgb",
        year: 2025,
        attributions: `© GeoBasis DE/LVGL-SL (2025), ${DL_DE_BY_2_0}`,
    },
    // Berlin: dl-zero-de/2.0, keine Namensnennung nötig, True-DOP 2026
    "11": {
        url: "https://gdi.berlin.de/services/wms/truedop_2026?",
        layer: "truedop_2026",
        year: 2026,
        attributions: "Luftbild: Geoportal Berlin",
    },
    // Brandenburg: dl-de/by-2-0, DOP20
    "12": {
        url: "https://isk.geobasis-bb.de/mapproxy/dop20c/service/wms?",
        layer: "bebb_dop20c",
        attributions: `© GeoBasis-DE/LGB, ${DL_DE_BY_2_0}`,
    },
    // Mecklenburg-Vorpommern: dl-de/by-2-0, DOP20, Vermerk mit Jahr (steht zusätzlich im Bild)
    "13": {
        url: "https://www.geodaten-mv.de/dienste/adv_dop?",
        layer: "mv_dop",
        attributions: `© GeoBasis-DE/M-V (${DATA_YEAR}), ${DL_DE_BY_2_0}`,
    },
    // Sachsen: dl-de/by-2-0, DOP20, Vermerk laut Nutzungsbedingungen GeoSN
    "14": {
        url: "https://geodienste.sachsen.de/wms_geosn_dop-rgb/guest?",
        layer: "sn_dop_020",
        attributions: `Quelle: GeoSN, ${DL_DE_BY_2_0}`,
    },
    // Sachsen-Anhalt: dl-de/by-2-0, DOP20
    "15": {
        url: "https://www.geodatenportal.sachsen-anhalt.de/wss/service/ST_LVermGeo_DOP_WMS_OpenData/guest?",
        layer: "lsa_lvermgeo_dop20_2",
        attributions: `© GeoBasis-DE/LVermGeo ST, ${DL_DE_BY_2_0}`,
    },
    // Thüringen: CC BY 4.0, DOP20
    "16": {
        url: "https://www.geoproxy.geoportal-th.de/geoproxy/services/DOP20?",
        layer: "th_dop",
        attributions: `© GDI-Th, ${CC_BY_4_0}`,
    },
};

/**
 * Liefert die Luftbild-Quelle des Bundeslands, in dem die Gemeinde liegt.
 * @param municipalityCode Gemeindeschlüssel (AGS); bei Ländern unter 10 fehlt die führende Null ("9163000")
 * @return die Quelle, oder null wenn für das Bundesland keine eingebunden ist
 */
export function aerialImageryForMunicipality(
    municipalityCode: string | number | null | undefined,
): AerialImagerySource | null {
    if (municipalityCode == null) return null;
    const ags = String(municipalityCode).padStart(8, "0");
    return AERIAL_IMAGERY_BY_STATE_PREFIX[ags.substring(0, 2)] ?? null;
}
